<template>
  <v-container fluid>
    <v-row>
      <v-col
        cols="12"
        v-if="documents.length === 0">
        <v-alert
          :value="true"
          type="info"
          class="white--text"
          transition="scale-transition">
          No {{ documentsName.toLowerCase() }} have been published.
        </v-alert>
      </v-col>
      <v-col
        cols="12"
        v-for="(documentCategory, i) in documents"
        :key="i">
        <v-card class="mb-3">
          <v-card-title class="font-weight-bold">{{ documentCategory.categoryName }}</v-card-title>

          <div
            v-for="(document, idx) in documentCategory.files"
            :key="document.id || idx">
            <v-card-text>
              <v-row
                align="center"
                class="mb-2">
                <v-col cols="auto">
                  <v-icon>{{ getFileIcon(document.filename) }}</v-icon>
                </v-col>
                <v-col>
                  <span class="font-weight-bold">{{ document.filename !== 'NULL' ? document.filename : 'Announcement' }}</span>
                  <span
                    class="caption ml-1"
                    v-if="document.description !== ''">- {{ document.description }}</span>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    small
                    text
                    color="primary"
                    @click="togglePreview(document)">
                    {{ isPreviewOpen(document) ? 'Hide Preview' : 'Show Preview' }}
                  </v-btn>
                  <v-btn
                    small
                    text
                    color="secondary"
                    :href="document.link"
                    target="_blank">
                    {{ getFileType(document.filename) === 'pdf' ? 'Download PDF' : 'Open Original' }}
                  </v-btn>
                </v-col>
              </v-row>

              <v-expand-transition>
                <div
                  v-if="isPreviewOpen(document)"
                  class="mt-3">
                  <div
                    v-if="isLoading(document)"
                    class="text-center pa-4">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      :size="50"
                    />
                    <div class="mt-2">Loading preview...</div>
                  </div>

                  <div
                    v-else-if="getFileType(document.filename) === 'pdf' && !isGoogleDoc(document.link) && getPdfSrc(document)"
                    class="document-preview">
                    <pdf-viewer :src="getPdfSrc(document)"/>
                  </div>

                  <div
                    v-else-if="getFileType(document.filename) === 'image' && !isEtapLink(document.link)"
                    class="document-preview">
                    <v-img
                      :src="document.link"
                      max-height="500"
                      contain/>
                  </div>

                  <div
                    v-else-if="getFileType(document.filename) === 'image' && documentBlobs[document.id || document.filename]"
                    class="document-preview">
                    <v-img
                      :src="documentBlobs[document.id || document.filename]"
                      max-height="500"
                      contain/>
                  </div>

                  <div
                    v-else-if="isGoogleDoc(document.link)"
                    class="document-preview">
                    <iframe
                      :src="getGoogleDocEmbedUrl(document.link)"
                      style="width: 100%; height: 500px; border: none;"
                      allowfullscreen/>
                  </div>

                  <div
                    v-else-if="document.filename === 'NULL' && document.description"
                    class="document-preview pa-3 grey lighten-4 rounded">
                    <div v-html="formatDescription(document.description)"/>
                  </div>

                  <div
                    v-else-if="documentErrors[document.id || document.filename]"
                    class="document-preview pa-3 error--text">
                    <v-alert
                      type="error"
                      text>
                      {{ documentErrors[document.id || document.filename] }}
                      Please use the "{{ getFileType(document.filename) === 'pdf' ? 'Download PDF' : 'Open Original' }}" button to view or download.
                    </v-alert>
                  </div>

                  <div
                    v-else
                    class="document-preview pa-3">
                    <p>This file type cannot be previewed inline. Please use the "{{ getFileType(document.filename) === 'pdf' ? 'Download PDF' : 'Open Original' }}" button to view or download.</p>
                  </div>
                </div>
              </v-expand-transition>
            </v-card-text>
            <v-divider/>
          </div>

          <div v-if="documentCategory.files.length === 0">
            <v-card-text>
              <v-icon/>
              <span class="ml-1">No {{ documentsName.toLowerCase() }} have been published.</span>
            </v-card-text>
            <v-divider/>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PdfViewer from './PdfViewer.vue'

export default {
  components: {
    PdfViewer
  },

  props: {
    documents: {
      type: Array,
      default: function () {
        return []
      }
    },
    documentsName: {
      type: String,
      default: 'Documents'
    }
  },

  data () {
    return {
      openPreviews: {},
      documentBlobs: {},
      loadingDocuments: {},
      documentErrors: {}
    }
  },

  beforeDestroy () {
    Object.values(this.documentBlobs).forEach(url => URL.revokeObjectURL(url))
  },

  methods: {
    getFileIcon: function (fileName) {
      if (!fileName || fileName === 'NULL') {
        return 'fa-bullhorn'
      }

      var fileExtension = this.getFileExtension(fileName)

      if (fileExtension === 'pdf') {
        return 'fa-file-pdf'
      } else if (['doc', 'docx'].includes(fileExtension)) {
        return 'fa-file-word'
      } else if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
        return 'fa-file-image'
      } else {
        return 'fa-file-alt'
      }
    },

    getFileExtension: function (fileName) {
      if (!fileName || fileName === 'NULL') return ''
      return fileName.split('.').pop().toLowerCase()
    },

    getFileType: function (fileName) {
      if (!fileName || fileName === 'NULL') return 'text'

      const ext = this.getFileExtension(fileName)

      if (ext === 'pdf') {
        return 'pdf'
      } else if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
        return 'image'
      } else if (['doc', 'docx'].includes(ext)) {
        return 'word'
      } else {
        return 'other'
      }
    },

    isGoogleDoc: function (link) {
      return link && (link.includes('docs.google.com') || link.includes('drive.google.com'))
    },

    getGoogleDocEmbedUrl: function (link) {
      return link.replace('/edit', '/preview')
    },

    isEtapLink: function (link) {
      return link && link.includes('etap.co.nz')
    },

    getPdfSrc: function (document) {
      if (this.getFileType(document.filename) !== 'pdf') return null
      const id = document.id || document.filename

      if (this.isEtapLink(document.link)) {
        return this.documentBlobs[id] || null
      }
      return document.link
    },

    togglePreview: function (document) {
      const id = document.id || document.filename
      const shouldOpen = !this.isPreviewOpen(document)

      this.$set(this.openPreviews, id, shouldOpen)

      if (shouldOpen && this.needsFetching(document) && !this.documentBlobs[id]) {
        this.fetchDocument(document)
      }
    },

    isPreviewOpen: function (document) {
      const id = document.id || document.filename
      return this.openPreviews[id] || false
    },

    isLoading: function (document) {
      const id = document.id || document.filename
      return this.loadingDocuments[id] || false
    },

    needsFetching: function (document) {
      const fileType = this.getFileType(document.filename)

      return (fileType === 'image' || fileType === 'pdf') && this.isEtapLink(document.link)
    },

    fetchDocument: function (document) {
      const id = document.id || document.filename
      this.$set(this.loadingDocuments, id, true)

      if (this.documentErrors[id]) {
        this.$delete(this.documentErrors, id)
      }

      const targetUrl = document.link

      fetch(targetUrl, {
        method: 'GET',
        headers: {
          'Accept': '*/*'
        }
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok: ' + response.status)
          }
          return response.blob()
        })
        .then(blob => {
          const typedBlob = this.getFileType(document.filename) === 'pdf'
            ? new Blob([blob], { type: 'application/pdf' })
            : blob
          const blobUrl = URL.createObjectURL(typedBlob)
          this.$set(this.documentBlobs, id, blobUrl)
        })
        .catch(error => {
          console.error('Error fetching document:', error)
          this.$set(this.documentErrors, id, 'Failed to load the document. ')
        })
        .finally(() => {
          this.$set(this.loadingDocuments, id, false)
        })
    },

    formatDescription: function (description) {
      return description
        .replace(/\r\n|\n|\r/g, '<br>')
        .replace(/\ud83d[\ude00-\udfff]|\ud83c[\udc00-\udfff]|\ud83e[\udd00-\udfff]/g, match => match)
    }
  }
}
</script>

<style scoped>
.document-preview {
  border-radius: 4px;
  overflow: hidden;
}
</style>
